// Pure-TS storage adapter for named customer selections on the
// /companies/stats admin page. Owns serialisation + shape validation
// only; sort.key validity against the live SortKey union is the
// caller's job at load time (so older entries with renamed keys
// survive a round-trip read→write intact).

export interface SavedSortSnapshot {
  key: string;
  direction: 'asc' | 'desc';
}

export interface SavedSelection {
  id: string;
  name: string;
  companyIds: string[];
  sort: SavedSortSnapshot;
  createdAt: string;
  updatedAt: string;
}

export const STORAGE_KEY = 'coright.admin.companyStats.savedSelections.v1';

const DEFAULT_SORT_REPAIR: SavedSortSnapshot = {
  key: 'signedAgreements',
  direction: 'desc',
};

const hasLocalStorage = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return typeof window.localStorage !== 'undefined' && window.localStorage !== null;
  } catch {
    // Accessing window.localStorage can throw in some sandboxed iframes.
    return false;
  }
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

interface NormalisedItem extends SavedSelection {
  [extraKey: string]: unknown;
}

// Unknown top-level keys from the last successful read, replayed on the
// next write so a v2-extended payload survives a v1 round-trip intact
// (mirrors the per-entry `...rest` spread in normaliseItem). Reset when
// load encounters malformed storage.
let lastTopLevelExtras: Record<string, unknown> = {};

const normaliseItem = (raw: unknown): NormalisedItem | null => {
  if (!isPlainObject(raw)) return null;

  const {
    id,
    name,
    companyIds,
    sort,
    createdAt,
    updatedAt,
    ...rest
  } = raw;

  if (typeof id !== 'string' || !id) return null;
  if (typeof name !== 'string') return null;
  if (!isStringArray(companyIds)) return null;
  if (typeof createdAt !== 'string') return null;
  if (typeof updatedAt !== 'string') return null;

  let normalisedSort: SavedSortSnapshot;
  if (sort === undefined) {
    // Defensive migration: pre-existing dev-build entries that lack
    // the sort field. There is no shipped v1 yet, but this keeps the
    // story explicit.
    normalisedSort = { ...DEFAULT_SORT_REPAIR };
  } else if (
    isPlainObject(sort) &&
    typeof sort.key === 'string' &&
    (sort.direction === 'asc' || sort.direction === 'desc')
  ) {
    normalisedSort = {
      ...(sort as Record<string, unknown>),
      key: sort.key,
      direction: sort.direction,
    } as SavedSortSnapshot;
  } else {
    return null;
  }

  return {
    ...rest,
    id,
    name,
    companyIds: [...companyIds],
    sort: normalisedSort,
    createdAt,
    updatedAt,
  };
};

export const loadSavedSelections = (): SavedSelection[] => {
  if (!hasLocalStorage()) {
    lastTopLevelExtras = {};
    return [];
  }

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    console.warn('savedSelectionsStorage: localStorage read failed', err);
    lastTopLevelExtras = {};
    return [];
  }
  if (raw === null) {
    lastTopLevelExtras = {};
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    console.warn('savedSelectionsStorage: malformed JSON', err);
    lastTopLevelExtras = {};
    return [];
  }

  if (!isPlainObject(parsed)) {
    console.warn('savedSelectionsStorage: top-level not an object; ignoring');
    lastTopLevelExtras = {};
    return [];
  }
  if (parsed.version !== 1) {
    console.warn('savedSelectionsStorage: unknown version; ignoring', parsed.version);
    lastTopLevelExtras = {};
    return [];
  }
  if (!Array.isArray(parsed.items)) {
    console.warn('savedSelectionsStorage: items not an array; ignoring');
    lastTopLevelExtras = {};
    return [];
  }

  const extras: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(parsed)) {
    if (k !== 'version' && k !== 'items') extras[k] = v;
  }
  lastTopLevelExtras = extras;

  const out: SavedSelection[] = [];
  let droppedCount = 0;
  for (const candidate of parsed.items) {
    const item = normaliseItem(candidate);
    if (item) out.push(item);
    else droppedCount += 1;
  }
  if (droppedCount > 0) {
    console.warn(
      `savedSelectionsStorage: dropped ${droppedCount} malformed item(s) during load`,
    );
  }
  return out;
};

export const writeSavedSelections = (items: SavedSelection[]): boolean => {
  if (!hasLocalStorage()) return false;
  try {
    const payload = JSON.stringify({ ...lastTopLevelExtras, version: 1, items });
    window.localStorage.setItem(STORAGE_KEY, payload);
    return true;
  } catch (err) {
    console.warn('savedSelectionsStorage: write failed', err);
    return false;
  }
};

export const newSelectionId = (): string => {
  if (typeof window !== 'undefined' && window.crypto) {
    const cryptoObj = window.crypto as Crypto & { randomUUID?: () => string };
    if (typeof cryptoObj.randomUUID === 'function') {
      try {
        return cryptoObj.randomUUID();
      } catch {
        // fall through to non-crypto fallback
      }
    }
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};
